import { captureException } from '@sentry/react';
import { StateCreator } from 'zustand';

import { userService } from '@/services/database/users';
import { isApiServiceError } from '@/services/http/errors';
import { publishLocalDeltaPacket } from '@/stores/realtime/delta';

import { UserActions, UserState } from '../types';

export const createUserActions: StateCreator<UserState & UserActions, [['zustand/immer', never]], [], UserActions> = (
    set,
    get,
) => ({
    // Core
    fetchUser: async () => {
        set((state) => {
            state.loadingUser = true;
            state.error = undefined;
        });

        try {
            const userResponse = await userService.getAuthenticated();

            set({
                selectedUser: userResponse.data,
                sessionMetadata: userResponse.metadata,
                session: userResponse.session,
                loadingUser: false,
            });

            return userResponse.data ?? undefined;
        } catch (error) {
            const isExpectedUnauthenticatedState =
                isApiServiceError(error) && (error.code === 'UNAUTHORIZED' || error.status === 401);

            if (!isExpectedUnauthenticatedState) {
                captureException(error);
            }

            set((state) => {
                state.selectedUser = null;
                state.sessionMetadata = null;
                state.session = null;
                state.error = isExpectedUnauthenticatedState ? undefined : 'Failed to fetch user detail';
                state.loadingUser = false;
            });
        }
    },
    clearSelectedUser: () => {
        set((state) => {
            state.selectedUser = null;
        });
    },
    updateSelectedUserLocal: (updates) => {
        set((state) => {
            if (state.selectedUser?.id) {
                Object.assign(state.selectedUser, updates);
            }
        });

        const userId = get().selectedUser?.id;

        if (userId) {
            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'user',
                operation: 'update',
                entityId: userId,
                payload: updates,
                actorUserId: userId,
                timestamp: Date.now(),
            });
        }
    },
    updateUser: async (updates) => {
        const prev = get().selectedUser ? structuredClone(get().selectedUser) : null;
        const userId = prev?.id;

        // if (!prev) {
        //     return;
        // }

        set((state) => {
            // const f = state.users.find((f) => f.id === id);

            // if (f) {
            //     Object.assign(f, updates);
            // }

            // Also update selectedUser if it matches
            if (state.selectedUser?.id) {
                Object.assign(state.selectedUser, updates);
            }
        });

        if (userId) {
            publishLocalDeltaPacket({
                v: 1,
                type: 'delta:packet',
                entity: 'user',
                operation: 'update',
                entityId: userId,
                payload: updates,
                actorUserId: userId,
                timestamp: Date.now(),
            });
        }

        try {
            const user = await userService.updateAuthenticated(updates);

            set((state) => {
                // const f = state.users.find((f) => f.id === id);

                // if (f) {
                //     Object.assign(f, user);
                // }

                if (state.selectedUser?.id) {
                    Object.assign(state?.selectedUser, user);
                }
            });

            if (user?.id) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'user',
                    operation: 'update',
                    entityId: user.id,
                    payload: user,
                    actorUserId: user.id,
                    timestamp: Date.now(),
                });
            }

            return user;
        } catch (error) {
            captureException(error);

            set((state) => {
                if (prev && state.selectedUser?.id === prev.id) {
                    Object.assign(state.selectedUser, prev);
                }

                state.error = 'Failed to update user';
            });

            if (prev?.id) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'user',
                    operation: 'update',
                    entityId: prev.id,
                    payload: prev,
                    actorUserId: prev.id,
                    timestamp: Date.now(),
                });
            }
        }
    },
    deleteUser: async (input) => {
        const prev = get().selectedUser ? structuredClone(get().selectedUser) : null;

        try {
            await userService.deleteAuthenticated(input);
            set({ selectedUser: null });

            if (prev?.id) {
                publishLocalDeltaPacket({
                    v: 1,
                    type: 'delta:packet',
                    entity: 'user',
                    operation: 'delete',
                    entityId: prev.id,
                    actorUserId: prev.id,
                    timestamp: Date.now(),
                });
            }
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to delete user' });
            throw error;
        }
    },
    applyUserDelta: (packet) => {
        if (packet.entity !== 'user') {
            return;
        }

        set((state) => {
            if (packet.operation === 'delete') {
                if (state.selectedUser?.id === packet.entityId) {
                    state.selectedUser = null;
                    state.session = {};
                    state.sessionMetadata = {};
                }

                return;
            }

            if (!packet.payload) {
                return;
            }

            if (state.selectedUser?.id === packet.entityId) {
                Object.assign(state.selectedUser, packet.payload);
            }
        });
    },
});
